import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AccountProvider } from './contexts/AccountContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Login from './components/Login';
import SsoCallback from './components/SsoCallback';
import UserList from './components/UserList';
import UserForm from './components/UserForm';
import AccountList from './components/AccountList';
import AccountForm from './components/AccountForm';
import MarketplaceOverrideFormPage from './components/MarketplaceOverrideFormPage';
import PartnerList from './components/PartnerList';
import PartnerForm from './components/PartnerForm';
import HotelForm from './components/HotelForm';
import HotelFullTemplateForm from './components/HotelFullTemplateForm';
import DvmExtractorListPage from './components/DvmExtractorListPage';
import DvmExtractorWizard from './components/DvmExtractorWizard';
import DvmExtractorDetailPage from './components/DvmExtractorDetailPage';
import HotelList from './components/HotelList';
import AppsPage from './components/AppsPage';
import IntegrateSystemsPage from './components/IntegrateSystemsPage';
import PipelineImplementationPage from './components/pipelines/PipelineImplementationPage';
import McpServicePage from './components/mcpservice/McpServicePage';
import McpHotelPage from './components/mcpservice/McpHotelPage';
import McpHotelWizard from './components/mcpservice/McpHotelWizard';
import McpAdminPage from './components/mcpservice/McpAdminPage';
import McpSourcePage from './components/mcpservice/McpSourcePage';
import UniversalApiPage from './components/UniversalApiPage';
import UapiAccessProfileFormPage from './components/UapiAccessProfileFormPage';
import ReceiversPollersPage from './components/ReceiversPollersPage';
import AddReceiverPollerPage from './components/AddReceiverPollerPage';
import CodePage from './components/CodePage';
import LegacyConnectorsPage from './components/LegacyConnectorsPage';
import UniversalApiDefinitionPage from './components/UniversalApiDefinitionPage';
import UapiPayloadsPage from './components/UapiPayloadsPage';
import UapiIntegrationsPage from './components/UapiIntegrationsPage';
import UapiIntegrationDetailPage from './components/UapiIntegrationDetailPage';
import UapiPayloadRedactionsPage from './components/UapiPayloadRedactionsPage';
import UapiAccessWizard from './components/UapiAccessWizard';
import UapiAccessDetailPage from './components/UapiAccessDetailPage';
import UapiAccessEditPage from './components/UapiAccessEditPage';
import UapiCustomCodePage from './components/UapiCustomCodePage';
import MarketplaceActionTypes from './components/MarketplaceActionTypes';
import Currencies from './components/Currencies';
import PaymentPorterPage from './components/PaymentPorterPage';
import PaymentPorterRulePage from './components/PaymentPorterRulePage';
import PaymentPorterNewRulePage from './components/PaymentPorterNewRulePage';
import PaymentPorterRunDetailPage from './components/PaymentPorterRunDetailPage';
import PaymentPorterCriteriaDetailPage from './components/PaymentPorterCriteriaDetailPage';
import PaymentPorterRunBetweenDatesPage from './components/PaymentPorterRunBetweenDatesPage';
import PaymentPorterListPage from './components/PaymentPorterListPage';
import OperaCloudCompareListPage from './components/OperaCloudCompareListPage';
import OperaCloudCompareWizard from './components/OperaCloudCompareWizard';
import OperaCloudCompareDetailPage from './components/OperaCloudCompareDetailPage';
import DryftHomePage from './components/DryftHomePage';
import DryftWizard from './components/DryftWizard';
import DryftDetailPage from './components/DryftDetailPage';
import DryftConfigSetForm from './components/DryftConfigSetForm';
import DryftNotificationTargetForm from './components/DryftNotificationTargetForm';
import RotanaPage from './components/RotanaPage';
import AddPaymentPorterWizard from './components/AddPaymentPorterWizard';
import OracleCloudPaymentPorterWizard from './components/OracleCloudPaymentPorterWizard';
import CorporateProfilesPage from './components/CorporateProfilesPage';
import AddCorporateProfilePage from './components/AddCorporateProfilePage';
import EditCorporateProfilePage from './components/EditCorporateProfilePage';
import ProfileIATAPage from './components/ProfileIATAPage';
import AddProfileIATAPage from './components/AddProfileIATAPage';
import EditProfileIATAPage from './components/EditProfileIATAPage';
import RateCodesPage from './components/RateCodesPage';
import AddRateCodePage from './components/AddRateCodePage';
import EditRateCodePage from './components/EditRateCodePage';
import PaymentMethodsPage from './components/PaymentMethodsPage';
import AddPaymentMethodPage from './components/AddPaymentMethodPage';
import EditPaymentMethodPage from './components/EditPaymentMethodPage';
import RoutingConfigPage from './components/RoutingConfigPage';
import AddRoutingPage from './components/AddRoutingPage';
import EditRoutingPage from './components/EditRoutingPage';
import PaymentPorterProcessorPage from './components/PaymentPorterProcessorPage';
import AddProfileAssignmentPage from './components/AddProfileAssignmentPage';
import ConnectorForm from './components/ConnectorForm';
import ConnectorIPRangeForm from './components/ConnectorIPRangeForm';
import ConnectorSettingForm from './components/ConnectorSettingForm';
import ConnectorTrxSummaryForm from './components/ConnectorTrxSummaryForm';
import VaultForm from './components/VaultForm';
import VaultDownloadsList from './components/VaultDownloadsList';
import VaultSourceList from './components/VaultSourceList';
import VaultSourceForm from './components/VaultSourceForm';
import VaultSourceGroupForm from './components/VaultSourceGroupForm';
import VaultTableForm from './components/VaultTableForm';
import VaultTableColumnForm from './components/VaultTableColumnForm';
import CanonicalList from './components/CanonicalList';
import CanonicalForm from './components/CanonicalForm';
import CanonicalGroupForm from './components/CanonicalGroupForm';
import CanonicalTemplateEditor from './components/CanonicalTemplateEditor';
import AdminConnectorsList from './components/AdminConnectorsList';
import ProspectsList from './components/ProspectsList';
import ConnectorTypes from './components/ConnectorTypes';
import ConnectorTypeForm from './components/ConnectorTypeForm';
import ConnectorTypeSettings from './components/ConnectorTypeSettings';
import ConnectorTypeSettingForm from './components/ConnectorTypeSettingForm';
import V2Connectors from './components/V2Connectors';
import V2ConnectorForm from './components/V2ConnectorForm';
import VaultPricing from './components/VaultPricing';
import ProductsList from './components/ProductsList';
import ProductPage from './components/ProductPage';
import DefaultRevShare from './components/DefaultRevShare';
import AdminSyncTemplates from './components/AdminSyncTemplates';
import AdminSyncScopes from './components/AdminSyncScopes';
import AdminSyncTemplateForm from './components/AdminSyncTemplateForm';
import AdminSyncTaskForm from './components/AdminSyncTaskForm';
import ProcessingStepsList from './components/ProcessingStepsList';
import S3Viewer from './components/S3Viewer';
import ProcessingStepsForm from './components/ProcessingStepsForm';
import MarketPlaceSettings from './components/MarketPlaceSettings';
import KnowledgeBaseAdmin from './components/KnowledgeBaseAdmin';
import MarketplaceForm from './components/MarketplaceForm';
import MarketplaceSettingForm from './components/MarketplaceSettingForm';
import Dashboard from './components/Dashboard';
import VaultList from './components/VaultList';
import LandingPage from './components/LandingPage';
import VaultQuery from './components/VaultQuery';
import Settings from './components/Settings';
import PartnerSettings from './components/PartnerSettings';
import BillingTemplatePage from './components/BillingTemplates';
import PartnerHotelChains from './components/PartnerHotelChains';
import PartnerStripeGuard from './components/PartnerStripeGuard';
import Profile from './components/Profile';
import SupportPage from './components/SupportPage';
import SupportTicketDetailPage from './components/SupportTicketDetailPage';
import InvitationAccept from './components/InvitationAccept';
import IntegrationForm from './components/IntegrationForm';
import IntegrationRouteCodePage from './components/IntegrationRouteCodePage';
import IntegrationRouteUapiCodePage from './components/IntegrationRouteUapiCodePage';
import TemplateEventDeliveryPage from './components/eventpoller/TemplateEventDeliveryPage';
import MarketingPack from './components/MarketingPack';
import OpenSearchAdmin from './components/OpenSearchAdmin';
import OpenSearchIndexDocs from './components/OpenSearchIndexDocs';
import CloudwatchLogs from './components/CloudwatchLogs';
import SprLogs from './components/SprLogs';
import AuditTrail from './components/AuditTrail';
import DnsAndIp from './components/DnsAndIp';
import PaymentTokenSwap from './components/PaymentTokenSwap';
import AthenaEditor from './components/AthenaEditor';
import OhipTransactionsPage from './components/OhipTransactionsPage';
import OhipHotelLinkWizard from './components/OhipHotelLinkWizard';
import LegacyConnectorLinkWizard from './components/LegacyConnectorLinkWizard';
import AdminSyncDiverter from './components/AdminSyncDiverter';
import AdminPegasusDiverter from './components/AdminPegasusDiverter';
import QuickBooksCustomersPage from './components/QuickBooksCustomersPage';
import QuickBooksCustomerLinkWizard from './components/QuickBooksCustomerLinkWizard';
import AccountsApproverRoute from './components/AccountsApproverRoute';
import ExtractionRunsPage from './components/ExtractionRunsPage';
import InvoicesPage from './components/InvoicesPage';
import OhipHotelGroupsPage from './components/OhipHotelGroupsPage';
import ToolkitPage from './components/ToolkitPage';
import AccountsAgentPage from './components/AccountsAgentPage';
import ToolkitBlocksPage from './components/ToolkitBlocksPage';
import ToolkitArAccountsPage from './components/ToolkitArAccountsPage';
import ToolkitArAccountTypesPage from './components/ToolkitArAccountTypesPage';
import ToolkitArLedgerPage from './components/ToolkitArLedgerPage';
import ToolkitGuestLedgerPage from './components/ToolkitGuestLedgerPage';
import ToolkitDepositLedgerPage from './components/ToolkitDepositLedgerPage';
import ToolkitDepositFolioPage from './components/ToolkitDepositFolioPage';
import ToolkitCheckInPage from './components/ToolkitCheckInPage';
import ToolkitOnPremPage from './components/ToolkitOnPremPage';
import ToolkitProfileResyncPage from './components/ToolkitProfileResyncPage';
import ToolkitHconnectOxiPage from './components/ToolkitHconnectOxiPage';
import ToolkitConfigComparePage from './components/ToolkitConfigComparePage';
import ToolkitOxiMappingPage from './components/ToolkitOxiMappingPage';
import ToolkitInternalNotesPage from './components/ToolkitInternalNotesPage';
import ToolkitUdfsPage from './components/ToolkitUdfsPage';
import ToolkitPepGuestBalancingPage from './components/ToolkitPepGuestBalancingPage';
import ToolkitPepGroupMasterPage from './components/ToolkitPepGroupMasterPage';
import ToolkitBlockGridPage from './components/ToolkitBlockGridPage';
import ToolkitEventManagementPage from './components/ToolkitEventManagementPage';
import ToolkitBalanceReservationsPage from './components/ToolkitBalanceReservationsPage';
import ToolkitCancellationCheckPage from './components/ToolkitCancellationCheckPage';
import ToolkitHistoryForecastPage from './components/ToolkitHistoryForecastPage';
import ToolkitArLedgerComparisonPage from './components/ToolkitArLedgerComparisonPage';
import ToolkitOooPage from './components/ToolkitOooPage';
import ToolkitHkDetailsPage from './components/ToolkitHkDetailsPage';
import ToolkitRefreshRatesPage from './components/ToolkitRefreshRatesPage';
import ToolkitSameDayDeparturesPage from './components/ToolkitSameDayDeparturesPage';
import ToolkitMarketCodesPage from './components/ToolkitMarketCodesPage';
import ToolkitCreateRoomsPage from './components/ToolkitCreateRoomsPage';
import SQSAdmin from './components/SQSAdmin';
import OxiDequeueAdmin from './components/OxiDequeueAdmin';
import AdminSyncConnectQueries from './components/AdminSyncConnectQueries';
import AdminLegacyIngress from './components/AdminLegacyIngress';
import AdminStreamingClients from './components/AdminStreamingClients';
import SyncPage from './components/SyncPage';
import SyncProjectDetailsPage from './components/SyncProjectDetailsPage';
import SyncTaskDetailsPage from './components/SyncTaskDetailsPage';
import SyncProjectFormPage from './components/SyncProjectFormPage';
import NoPermissionsPage from './components/NoPermissionsPage';
import TaskConfigurationPage from './components/TaskConfigurationPage';
import ConversionCodeEditPage from './components/ConversionCodeEditPage';
import VendorDetailPage from './components/VendorDetailPage';
import VendorSettings from './components/VendorSettings';
import ErrorAnalysisPage from './components/ErrorAnalysisPage';
import SyncReportPage from './components/SyncReportPage';
import OnsiteInstructionsPage from './components/OnsiteInstructionsPage';
import ProjectOnsiteReportPage from './components/ProjectOnsiteReportPage';
import TaskReconciliationPage from './components/TaskReconciliationPage';
import MessageLogPage from './components/MessageLogPage';
import PaymentMigrationManagerListPage from './components/PaymentMigrationManagerListPage';
import PaymentMigrationManagerPage from './components/PaymentMigrationManagerPage';
import AddPaymentMigrationManagerWizard from './components/AddPaymentMigrationManagerWizard';
import PaymentMigrationManagerRunPage from './components/PaymentMigrationManagerRunPage';
import PaymentMigrationManagerRunDetailPage from './components/PaymentMigrationManagerRunDetailPage';
import PMMAddCorporateProfilePage from './components/PMMAddCorporateProfilePage';
import PMMAddIataProfilePage from './components/PMMAddIataProfilePage';
import PMMAddRateCodePage from './components/PMMAddRateCodePage';
import PMMAddPaymentMethodPage from './components/PMMAddPaymentMethodPage';
import PMMAddProfileAssignmentPage from './components/PMMAddProfileAssignmentPage';
import PMMAddRoutingPage from './components/PMMAddRoutingPage';
import PMMAddRoutingCodePage from './components/PMMAddRoutingCodePage';
import ContractsList from './components/ContractsList';
import ContractForm from './components/ContractForm';
import { useAuth } from './contexts/AuthContext';
import './App.css';

// Old Legacy > Universal API links -> the same page under /universal-api, path and query preserved.
const PartnerBillingRedirect = () => {
  const { pathname } = useLocation();
  const partnerId = pathname.split('/')[2];
  return <Navigate to={`/partners/${partnerId}?tab=billing`} replace />;
};

const LegacyUapiRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace('/legacy/universal-api', '/universal-api')}${search}${hash}`} replace />;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, pendingInvitations, user } = useAuth();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/';
  const hasRedirectedRef = React.useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      hasRedirectedRef.current = false;
      return;
    }
    if (pendingInvitations.length > 0 && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      navigate('/invitations', { replace: true });
    }
  }, [isAuthenticated, pendingInvitations.length]);

  return (
    <div className={`App ${isLoginPage ? 'login-page' : ''}`}>
      <Navbar />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/sso-callback" element={<SsoCallback />} />
        <Route path="/no-permissions" element={<NoPermissionsPage />} />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />

            <Route
              path="/invitations"
              element={
                <PrivateRoute>
                  <InvitationAccept />
                </PrivateRoute>
              }
            />

            <Route
              path="/support"
              element={
                <PrivateRoute>
                  <SupportPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/support/:id"
              element={
                <PrivateRoute>
                  <SupportTicketDetailPage />
                </PrivateRoute>
              }
            />

            {/* THE TEMPLATE LIST IS THE PIPELINES PAGE'S Pipeline Templates tab. This address showed the old Integrations
                list (a From / To connector per template, the pre-canvas model) — "Unknown" for every template today. */}
            <Route path="/pipeline-templates" element={<Navigate to="/pipelines?tab=templates" replace />} />

            <Route
              path="/pipeline-templates/create"
              element={
                <PrivateRoute>
                  <IntegrationForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/pipeline-templates/:id"
              element={
                <PrivateRoute>
                  <IntegrationForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/pipeline-templates/:id/edit"
              element={
                <PrivateRoute>
                  <IntegrationForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Custom code on one integration route — opened from the route's Custom Code button. */}
            <Route
              path="/pipeline-templates/:id/routes/:routeId/code"
              element={
                <PrivateRoute>
                  <IntegrationRouteCodePage />
                </PrivateRoute>
              }
            />

            {/* The Universal API custom code a template runs around one route's target integration (V533) —
                opened from the Custom Code button in the designer's Integration column. */}
            <Route
              path="/pipeline-templates/:id/routes/:routeId/integration-code"
              element={
                <PrivateRoute>
                  <IntegrationRouteUapiCodePage />
                </PrivateRoute>
              }
            />

            {/* How the source's events reach the hub (V686) — polled or streamed, set on the template. Superadmin. */}
            <Route
              path="/pipeline-templates/:id/events"
              element={
                <PrivateRoute>
                  <TemplateEventDeliveryPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/hotels"
              element={
                <PrivateRoute>
                  <HotelList />
                </PrivateRoute>
              }
            />

            <Route
              path="/vaults"
              element={
                <PrivateRoute>
                  <VaultList />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps"
              element={
                <PrivateRoute>
                  <AppsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/pipelines"
              element={
                <PrivateRoute requireSuperAdmin>
                  <IntegrateSystemsPage />
                </PrivateRoute>
              }
            />

            {/* ONE HOTEL'S PIPELINE — a template at a hotel (an implementation), opened from Pipelines › Hotels. */}
            <Route
              path="/pipelines/:templateId/implementations/:implementationId"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PipelineImplementationPage />
                </PrivateRoute>
              }
            />

            {/* MCP AS A SERVICE — the tenant console, for the chain and hotel admins who run it (the API decides who
                may change what). Superadmin only for now (HIL-140); drop requireSuperAdmin here, in the Navbar,
                Chain Settings › MCP, the hotel's Products › MCP and the pipelines API gate to open it up. */}
            <Route
              path="/mcp-service"
              element={
                <PrivateRoute requireSuperAdmin>
                  <McpServicePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/mcp-service/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <McpHotelWizard />
                </PrivateRoute>
              }
            />
            <Route
              path="/mcp-service/hotels/:hotelId"
              element={
                <PrivateRoute requireSuperAdmin>
                  <McpHotelPage />
                </PrivateRoute>
              }
            />

            {/* WHAT THIS SCREEN HAS BEEN CALLED. ESB until 2026-09-15, Integrate Systems until
                2026-09-24, Pipelines now. Every old address is kept as a redirect rather than deleted:
                bookmarks, links in tickets and anything already pointing here should land on the screen
                rather than on a 404 that says nothing about where it went.

                The templates moved with it — /integrations/* is now /pipeline-templates/*, and
                LegacyPathRedirect carries the rest of the path across so a link to one template's edit
                page still opens that template. */}
            <Route path="/esb" element={<Navigate to="/pipelines" replace />} />
            <Route path="/integrate-systems" element={<Navigate to="/pipelines" replace />} />
            <Route path="/integrations/*" element={<LegacyPathRedirect from="/integrations" to="/pipeline-templates" />} />

            <Route
              path="/universal-api"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UniversalApiPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/receivers-pollers"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ReceiversPollersPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/receivers-pollers/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AddReceiverPollerPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/receivers-pollers/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AddReceiverPollerPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/code"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CodePage />
                </PrivateRoute>
              }
            />

            {/* Legacy — pre-hub connector maintenance. Superadmin only, matching the sidebar entry:
                the route must be guarded too, or the page is reachable by typing the URL. */}
            <Route
              path="/legacy/connectors"
              element={
                <PrivateRoute requireSuperAdmin>
                  <LegacyConnectorsPage />
                </PrivateRoute>
              }
            />

            {/* The Universal API moved out of Legacy to /universal-api (2026-09-07). These paths have
                been handed out — bookmarked, linked from tickets — so they redirect rather than 404,
                and the splat carries the rest of the path so a deep link to one property's access
                lands on that property rather than on the section index. `replace` keeps the old URL
                out of the history, so Back does not bounce off the redirect. */}
            <Route path="/legacy/universal-api/*" element={<LegacyUapiRedirect />} />

            {/* Legacy > Universal API > Authentication — which properties are provisioned to call the
                uAPI, and their credentials. Scoped by the hotel picker: no hotel lists the estate. */}
            {/* The access list is a TAB on the Universal API screen, not a page of its own — so this
                path renders the same component as /universal-api, which picks the tab from the URL.
                The path is kept because it has been handed out and because a tab that cannot be linked
                to is a tab the Back button cannot return you to. */}
            <Route
              path="/universal-api/authentication"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UniversalApiPage />
                </PrivateRoute>
              }
            />

            {/* Access Profiles: which properties may reach the API, what they route to and what is
                granted on them. Promoted from a sub-tab under Authentication to a tab of its own — it
                answers a different question from "which credential does this hotel hold", and burying
                it one level down meant nobody found it. Same component, tab picked from the URL. */}
            <Route
              path="/universal-api/access-profiles"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UniversalApiPage />
                </PrivateRoute>
              }
            />

            {/* Authoring one profile. `new` and `:id` are the same component — creating and editing a
                profile are the same form, and splitting them would be two screens to keep in step.
                Declared BEFORE `/:id` would match `new`, which is why `new` is listed first. */}
            <Route
              path="/universal-api/access-profiles/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiAccessProfileFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/universal-api/access-profiles/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiAccessProfileFormPage />
                </PrivateRoute>
              }
            />

            {/* Redactions: every set on the estate, grouped by the document it redacts. A tab of its
                own because the question it answers — where is this field struck, and by whom — cannot
                be asked from a single payload's screen. */}
            <Route
              path="/universal-api/redactions"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UniversalApiPage />
                </PrivateRoute>
              }
            />

            {/* Custom Code — per-client before/after hooks around a backend's integration (V528). The list
                is a tab (same component, tab from the URL); one snippet is a page of its own. `new` is
                declared before `:id` so it is not read as an id. ?accessId= says which backend binding it
                was opened from — a new snippet is attached to it, and Back returns there. */}
            <Route
              path="/universal-api/custom-code"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UniversalApiPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/universal-api/custom-code/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiCustomCodePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/universal-api/custom-code/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiCustomCodePage />
                </PrivateRoute>
              }
            />

            {/* Logs is a tab too, and gets a path for the same reasons — linkable, and the Back button
                returns to the tab you were on rather than to the surface. */}
            <Route
              path="/universal-api/logs"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UniversalApiPage />
                </PrivateRoute>
              }
            />

            {/* Provisioning a property: its inbound uAPI credential, and the backend system the API
                calls for it. A full page, not a modal — it is a multi-step flow with its own back
                navigation, and the issued secret has to be readable on a screen of its own. */}
            <Route
              path="/universal-api/authentication/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiAccessWizard />
                </PrivateRoute>
              }
            />

            {/* One property's access: its Universal API credential, and every backend system it is
                routed to with that system's own x-hotelid. */}
            <Route
              path="/universal-api/authentication/:hotelId"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiAccessDetailPage />
                </PrivateRoute>
              }
            />

            {/* One backend-system binding: the credential the Universal API calls it with, and the
                per-hotel values that credential needs. */}
            <Route
              path="/universal-api/authentication/:hotelId/access/:accessId"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiAccessEditPage />
                </PrivateRoute>
              }
            />

            {/* The same wizard, entered from a hotel that already has access — the property is settled,
                so it opens on the backend-system step. */}
            <Route
              path="/universal-api/authentication/:hotelId/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiAccessWizard />
                </PrivateRoute>
              }
            />

            <Route
              path="/rotana"
              element={
                <PrivateRoute>
                  <RotanaPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter"
              element={
                <PrivateRoute>
                  <PaymentPorterListPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/opera-cloud-compare"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OperaCloudCompareListPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/opera-cloud-compare/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OperaCloudCompareWizard />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/opera-cloud-compare/:id/pay"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OperaCloudCompareWizard />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/opera-cloud-compare/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OperaCloudCompareDetailPage />
                </PrivateRoute>
              }
            />

            {/* DVM Extractor — superadmin only for now. The card on /apps is hidden for everyone else
                and the lambda refuses them too; hiding a card does not stop a URL being typed. */}
            <Route
              path="/apps/dvm-extractor"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DvmExtractorListPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dvm-extractor/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DvmExtractorWizard />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dvm-extractor/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DvmExtractorDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dryft"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DryftHomePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dryft/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DryftWizard />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dryft/config-sets/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DryftConfigSetForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dryft/config-sets/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DryftConfigSetForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dryft/notification-targets/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DryftNotificationTargetForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dryft/notification-targets/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DryftNotificationTargetForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/dryft/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DryftDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/new"
              element={
                <PrivateRoute requireAdmin>
                  <PartnerStripeGuard>
                    <AddPaymentPorterWizard />
                  </PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/oracle-cloud/new"
              element={
                <PrivateRoute requireAdmin>
                  <OracleCloudPaymentPorterWizard />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:id"
              element={
                <PrivateRoute>
                  <PaymentPorterPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/runs/:runUuid"
              element={
                <PrivateRoute>
                  <PaymentPorterRunDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/run-between-dates"
              element={
                <PrivateRoute>
                  <PaymentPorterRunBetweenDatesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/rules/new"
              element={
                <PrivateRoute>
                  <PaymentPorterNewRulePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/rules/:ruleId/matched-reservations"
              element={
                <PrivateRoute>
                  <PaymentPorterCriteriaDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/rules/:ruleId"
              element={
                <PrivateRoute>
                  <PaymentPorterRulePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/corporate-profiles"
              element={
                <PrivateRoute>
                  <CorporateProfilesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/corporate-profiles/new"
              element={
                <PrivateRoute>
                  <AddCorporateProfilePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/corporate-profiles/:profileId/edit"
              element={
                <PrivateRoute>
                  <EditCorporateProfilePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/profile-iata"
              element={
                <PrivateRoute>
                  <ProfileIATAPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/profile-iata/new"
              element={
                <PrivateRoute>
                  <AddProfileIATAPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/profile-iata/:profileId/edit"
              element={
                <PrivateRoute>
                  <EditProfileIATAPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/rate-codes"
              element={
                <PrivateRoute>
                  <RateCodesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/rate-codes/new"
              element={
                <PrivateRoute>
                  <AddRateCodePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/rate-codes/:rateCodeId/edit"
              element={
                <PrivateRoute>
                  <EditRateCodePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/payment-methods"
              element={
                <PrivateRoute>
                  <PaymentMethodsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/payment-methods/new"
              element={
                <PrivateRoute>
                  <AddPaymentMethodPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/payment-methods/:methodId/edit"
              element={
                <PrivateRoute>
                  <EditPaymentMethodPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/routing"
              element={
                <PrivateRoute>
                  <RoutingConfigPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/routing/new"
              element={
                <PrivateRoute>
                  <AddRoutingPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/routing/:routingId/edit"
              element={
                <PrivateRoute>
                  <EditRoutingPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/processor"
              element={
                <PrivateRoute>
                  <PaymentPorterProcessorPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/profile-assignments/new"
              element={
                <PrivateRoute>
                  <AddProfileAssignmentPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-porter/:ppId/profile-assignments/:assignmentId/edit"
              element={
                <PrivateRoute>
                  <AddProfileAssignmentPage />
                </PrivateRoute>
              }
            />

            {/* Payment Migration Manager Routes */}
            <Route
              path="/apps/payment-migration-manager"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PaymentMigrationManagerListPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AddPaymentMigrationManagerWizard />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/run"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PaymentMigrationManagerRunPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/runs/:runRef"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PaymentMigrationManagerRunDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/corporate-profiles/add"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PMMAddCorporateProfilePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/iata-profiles/add"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PMMAddIataProfilePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/rate-codes/add"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PMMAddRateCodePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/payment-methods/add"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PMMAddPaymentMethodPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/profile-assignments/add"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PMMAddProfileAssignmentPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/routing/add"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PMMAddRoutingPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id/routing-codes/add"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PMMAddRoutingCodePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps/payment-migration-manager/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PaymentMigrationManagerPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/vaults/new"
              element={
                <PrivateRoute requireAdmin>
                  <PartnerStripeGuard><VaultForm mode="create" /></PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/vaults/:id"
              element={
                <PrivateRoute>
                  <VaultQuery />
                </PrivateRoute>
              }
            />

            <Route
              path="/users"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UserList />
                </PrivateRoute>
              }
            />

            <Route
              path="/users/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UserForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/users/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UserForm mode="edit" />
                </PrivateRoute>
              }
            />

            <Route
              path="/users/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UserForm mode="edit" />
                </PrivateRoute>
              }
            />

            <Route
              path="/accounts/:id"
              element={
                <PrivateRoute>
                  <AccountForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/accounts/:id/edit"
              element={
                <PrivateRoute>
                  <AccountForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Marketplace Overrides — add and edit are pages, not modals, and superadmin-only in the
                same way the rest of marketplace configuration is. */}
            <Route
              path="/accounts/:id/marketplace-overrides/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceOverrideFormPage entityType="account" />
                </PrivateRoute>
              }
            />

            <Route
              path="/accounts/:id/marketplace-overrides/:overrideId"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceOverrideFormPage entityType="account" />
                </PrivateRoute>
              }
            />

            <Route
              path="/partners"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PartnerList />
                </PrivateRoute>
              }
            />

            <Route
              path="/partners/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PartnerForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/partners/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PartnerForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/partners/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PartnerForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Partner Addresses was retired for the Billing tab (V564). A link to add one lands on Billing. */}
            <Route path="/partners/:partnerid/address/new" element={<PartnerBillingRedirect />} />

            <Route
              path="/partners/:partnerid/account/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PartnerStripeGuard><AccountForm mode="create" /></PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/accounts/:accountid/hotel/new"
              element={
                <PrivateRoute>
                  <PartnerStripeGuard><HotelForm mode="create" /></PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/hotel/new"
              element={
                <PrivateRoute>
                  <PartnerStripeGuard><HotelForm mode="create" /></PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/hotel/:id"
              element={
                <PrivateRoute>
                  <HotelForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/hotel/:id/edit"
              element={
                <PrivateRoute>
                  <HotelForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Full document templates get their own screen: the editor, its preview and the assistant
                conversation do not fit under a table, and the work is long enough that it must survive a
                misclick on the list behind it. */}
            <Route
              path="/hotel/:hotelId/fulltemplate/new"
              element={
                <PrivateRoute>
                  <HotelFullTemplateForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/hotel/:hotelId/fulltemplate/:id"
              element={
                <PrivateRoute>
                  <HotelFullTemplateForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/hotel/:hotelid/vault/new"
              element={
                <PrivateRoute requireAdmin>
                  <PartnerStripeGuard><VaultForm mode="create" /></PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/vault/:id"
              element={
                <PrivateRoute>
                  <VaultForm mode="edit" />
                </PrivateRoute>
              }
            />

            <Route
              path="/vault/:id/edit"
              element={
                <PrivateRoute>
                  <VaultForm mode="edit" />
                </PrivateRoute>
              }
            />

            <Route
              path="/vault/:vaultid/downloads"
              element={
                <PrivateRoute>
                  <VaultDownloadsList />
                </PrivateRoute>
              }
            />

            <Route
              path="/hotel/:hotelid/connector/new"
              element={
                <PrivateRoute>
                  <ConnectorForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connectors/new"
              element={
                <PrivateRoute>
                  <ConnectorForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connectors/:id"
              element={
                <PrivateRoute>
                  <ConnectorForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connectors/:id/edit"
              element={
                <PrivateRoute>
                  <ConnectorForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* IP Range Routes */}
            <Route
              path="/connector/:connectorid/iprange/new"
              element={
                <PrivateRoute>
                  <ConnectorIPRangeForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connector/:connectorid/iprange/:id"
              element={
                <PrivateRoute>
                  <ConnectorIPRangeForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connector/:connectorid/iprange/:id/edit"
              element={
                <PrivateRoute>
                  <ConnectorIPRangeForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Setting Routes */}
            <Route
              path="/connector/:connectorid/setting/new"
              element={
                <PrivateRoute>
                  <ConnectorSettingForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connector/:connectorid/setting/:id"
              element={
                <PrivateRoute>
                  <ConnectorSettingForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connector/:connectorid/setting/:id/edit"
              element={
                <PrivateRoute>
                  <ConnectorSettingForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Transaction Summary Routes */}
            <Route
              path="/connector/:connectorid/trxsummary/new"
              element={
                <PrivateRoute>
                  <ConnectorTrxSummaryForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connector/:connectorid/trxsummary/:id"
              element={
                <PrivateRoute>
                  <ConnectorTrxSummaryForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/connector/:connectorid/trxsummary/:id/edit"
              element={
                <PrivateRoute>
                  <ConnectorTrxSummaryForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Vault Source Routes */}
            <Route
              path="/admin/vaults"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultSourceList />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultSourceForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultSourceForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultSourceForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Vault Source Group Routes */}
            <Route
              path="/admin/vaults/:vaultsourceid/vaultsourcegroup/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultSourceGroupForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:vaultsourceid/vaultsourcegroup/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultSourceGroupForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:vaultsourceid/vaultsourcegroup/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultSourceGroupForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Vault Table Routes */}
            <Route
              path="/admin/vaults/:vaultsourceid/vaulttable/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultTableForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:vaultsourceid/vaulttable/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultTableForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:vaultsourceid/vaulttable/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultTableForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Vault Table Column Routes */}
            <Route
              path="/admin/vaults/:vaultsourceid/vaulttable/:vaulttableid/vaulttablecolumn/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultTableColumnForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:vaultsourceid/vaulttable/:vaulttableid/vaulttablecolumn/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultTableColumnForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/vaults/:vaultsourceid/vaulttable/:vaulttableid/vaulttablecolumn/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <VaultTableColumnForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Canonical Routes */}
            <Route
              path="/admin/canonical"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalList />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/canonical/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/canonical/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/canonical/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Canonical Group Routes */}
            <Route
              path="/admin/canonical/:canonicalid/canonicalgroup/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalGroupForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/canonical/:canonicalid/canonicalgroup/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalGroupForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/canonical/:canonicalid/canonicalgroup/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalGroupForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Canonical Template Routes */}
            <Route
              path="/admin/canonical/:canonicalid/canonicalgroup/:groupid/template/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalTemplateEditor mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/canonical/:canonicalid/canonicalgroup/:groupid/template/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CanonicalTemplateEditor mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Connectors Route */}
            <Route
              path="/admin/connectors"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminConnectorsList />
                </PrivateRoute>
              }
            />

            {/* Admin - Prospects Route */}
            <Route
              path="/admin/prospects"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ProspectsList />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/contracts"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ContractsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/contracts/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ContractForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/contracts/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ContractForm />
                </PrivateRoute>
              }
            />

            {/* Admin - Connector Types Routes */}
            <Route
              path="/admin/connector-types"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypes />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/connector-types/create"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypeForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/connector-types/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypeForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/connector-types/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypeForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Connector Type Settings Routes (nested under connector types) */}
            <Route
              path="/admin/connector-types/:connectorTypeId/settings"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypeSettings />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/connector-types/:connectorTypeId/settings/create"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypeSettingForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/connector-types/:connectorTypeId/settings/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypeSettingForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/connector-types/:connectorTypeId/settings/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ConnectorTypeSettingForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Infra - Legacy Ingress: what still calls the legacy stack */}
            <Route
              path="/admin/legacy-ingress"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminLegacyIngress />
                </PrivateRoute>
              }
            />
            {/* Admin - Infra - Sync Diverter: legacy OXI API keys forwarded to the hub (sync_diverter, legacy db) */}
            <Route
              path="/admin/sync-diverter"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncDiverter />
                </PrivateRoute>
              }
            />
            {/* Admin - Infra - Pegasus Diverter: legacy Pegasus sources forwarded to the hub (pegasus_diverter, legacy db) */}
            <Route
              path="/admin/pegasus-diverter"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminPegasusDiverter />
                </PrivateRoute>
              }
            />
            {/* Link legacy OHIP connectors to hub partner > chain > hotel — incremental, run as often as needed */}
            <Route
              path="/admin/legacy-ingress/ohip/link"
              element={
                <PrivateRoute requireSuperAdmin>
                  <LegacyConnectorLinkWizard />
                </PrivateRoute>
              }
            />

            {/* Admin - Infra - Streaming Clients: the hub-run streaming consumers on EKS */}
            <Route
              path="/admin/streaming-clients"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminStreamingClients />
                </PrivateRoute>
              }
            />

            {/* Admin - V2 Connectors Routes */}
            <Route
              path="/admin/v2-connectors"
              element={
                <PrivateRoute requireSuperAdmin>
                  <V2Connectors />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/v2-connectors/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <V2ConnectorForm mode="create" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/v2-connectors/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <V2ConnectorForm mode="view" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/v2-connectors/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <V2ConnectorForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Products Routes */}
            <Route
              path="/admin/products"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ProductsList />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/products/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ProductPage />
                </PrivateRoute>
              }
            />

            {/* Admin - Default Rev Share Route */}
            <Route
              path="/admin/default-rev-share"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DefaultRevShare />
                </PrivateRoute>
              }
            />

            {/* Admin - Sync Templates Route */}
            <Route
              path="/admin/sync-templates"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncTemplates />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/sync-templates/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncTemplateForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/sync-templates/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncTemplateForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/sync-templates/:id/tasks/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncTaskForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/sync-templates/:id/tasks/:taskId/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncTaskForm />
                </PrivateRoute>
              }
            />

            {/* Admin - Sync Scopes Route */}
            <Route
              path="/admin/sync-scopes"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncScopes />
                </PrivateRoute>
              }
            />

            {/* Admin - OpenSearch Routes */}
            <Route
              path="/admin/opensearch"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OpenSearchAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/opensearch/indices/:indexName"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OpenSearchIndexDocs />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/opensearch/cwlogs"
              element={
                <PrivateRoute requireSuperAdmin>
                  <CloudwatchLogs />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/opensearch/spr-logs"
              element={
                <PrivateRoute requireSuperAdmin>
                  <SprLogs />
                </PrivateRoute>
              }
            />
            {/* Admin › Platform › Audit Trail — searches OpenSearch and the S3 archive as one. Moved from Admin › OpenSearch;
                the old address still lands here. */}
            <Route
              path="/admin/audit-trail"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AuditTrail />
                </PrivateRoute>
              }
            />
            <Route path="/admin/opensearch/audit-trail" element={<Navigate to="/admin/audit-trail" replace />} />

            <Route
              path="/admin/dns-and-ip"
              element={
                <PrivateRoute requireSuperAdmin>
                  <DnsAndIp />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/payment-token-swap"
              element={
                <PrivateRoute requireSuperAdmin>
                  <PaymentTokenSwap />
                </PrivateRoute>
              }
            />





            <Route
              path="/admin/billing/hotel-groups"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OhipHotelGroupsPage />
                </PrivateRoute>
              }
            />

            {/* Admin - Billing - OHIP Transactions.
                Superadmin-only: it is served by the /admin/* API, which has no non-superadmin path. */}
            {/* Admin - Billing - Accounts Agent (code-accounts, /finance* API). Superadmin-only screen; deciding
                actions is further limited to the accounts approvers by the API. */}
            <Route
              path="/admin/billing/accounts-agent"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AccountsApproverRoute><AccountsAgentPage /></AccountsApproverRoute>
                </PrivateRoute>
              }
            />
            {/* Admin - Billing - QuickBooks Customers: read-only list from the accounts agent's QuickBooks connection. */}
            <Route
              path="/admin/billing/quickbooks-customers"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AccountsApproverRoute><QuickBooksCustomersPage /></AccountsApproverRoute>
                </PrivateRoute>
              }
            />
            {/* Find QuickBooks customers not yet linked to an hconnect chain/hotel and link them. */}
            <Route
              path="/admin/billing/quickbooks-customers/link"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AccountsApproverRoute><QuickBooksCustomerLinkWizard /></AccountsApproverRoute>
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/billing/ohip-transactions"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OhipTransactionsPage />
                </PrivateRoute>
              }
            />
            {/* Link OHIP property codes to hub hotels and set each billing group's bill-to. */}
            <Route
              path="/admin/billing/ohip-transactions/link-hotels"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OhipHotelLinkWizard />
                </PrivateRoute>
              }
            />

            {/* Admin - Billing - Extraction Runs. Superadmin-only for the same reason as the two above:
                served by the /admin/* API, which has no non-superadmin path. */}
            <Route
              path="/admin/billing/extraction-runs"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ExtractionRunsPage />
                </PrivateRoute>
              }
            />

            {/* Admin - Billing - Invoices. Superadmin-only, like the rest of Billing: served by the
                /admin/* API, which has no non-superadmin path. */}
            <Route
              path="/admin/billing/invoices"
              element={
                <PrivateRoute requireSuperAdmin>
                  <InvoicesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/athena"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AthenaEditor />
                </PrivateRoute>
              }
            />

            {/* Admin - SQS Route */}
            <Route
              path="/admin/sqs"
              element={
                <PrivateRoute requireSuperAdmin>
                  <SQSAdmin />
                </PrivateRoute>
              }
            />

            {/* Admin - Platform - SyncConnect ad-hoc queries */}
            <Route
              path="/admin/syncconnect"
              element={
                <PrivateRoute requireSuperAdmin>
                  <AdminSyncConnectQueries />
                </PrivateRoute>
              }
            />

            {/* Admin - OXI Dequeue Queue Route */}
            <Route
              path="/admin/oxi-dequeue-queue"
              element={
                <PrivateRoute requireSuperAdmin>
                  <OxiDequeueAdmin />
                </PrivateRoute>
              }
            />

            {/* Admin - S3 Viewer */}
            <Route
              path="/admin/s3-viewer"
              element={
                <PrivateRoute requireSuperAdmin>
                  <S3Viewer />
                </PrivateRoute>
              }
            />
            {/* Admin - Processing Steps Routes */}
            <Route
              path="/admin/sync-processing-steps"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ProcessingStepsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/sync-processing-steps/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ProcessingStepsForm mode="create" />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/sync-processing-steps/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ProcessingStepsForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Knowledge Base Route */}
            <Route
              path="/admin/knowledge-base"
              element={
                <PrivateRoute requireSuperAdmin>
                  <KnowledgeBaseAdmin />
                </PrivateRoute>
              }
            />

            {/* Admin - Marketplace Route */}
            <Route
              path="/admin/marketplace"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketPlaceSettings />
                </PrivateRoute>
              }
            />

            {/* Admin - Marketplace Create Route */}
            <Route
              path="/admin/marketplace/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceForm mode="create" />
                </PrivateRoute>
              }
            />

            {/* Admin - Marketplace Edit Route */}
            <Route
              path="/admin/marketplace/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Admin - Universal API DEFINITION (routes + operations). Superadmin: a route added here
                changes the API for every property, which is not a per-customer edit. */}
            <Route
              path="/admin/universal-api"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UniversalApiDefinitionPage />
                </PrivateRoute>
              }
            />

            {/* The payloads a module carries, on their own screen — the same component the route tree
                uses, so importing and downloading behave identically in both places. */}
            <Route
              path="/admin/universal-api/payloads"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiPayloadsPage />
                </PrivateRoute>
              }
            />

            {/* The code that talks to each backend system: a file tree per system, versioned in the
                database and run by the Universal API without a deploy. Superadmin-only — an integration
                belongs to a SYSTEM, not to a property, so there is no hotel context to scope it by. */}
            <Route
              path="/admin/universal-api/integrations"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiIntegrationsPage />
                </PrivateRoute>
              }
            />

            {/* One integration: its file tree, its versions, and the properties pinned to a candidate.
                A screen of its own rather than a modal — this is where code is written. */}
            <Route
              path="/admin/universal-api/integrations/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiIntegrationDetailPage />
                </PrivateRoute>
              }
            />

            {/* What to strip from one payload before it leaves the Universal API. A screen of its own
                rather than a modal: the document is hundreds of fields deep and choosing what to redact
                means reading it. */}
            <Route
              path="/admin/universal-api/payloads/:id/redactions"
              element={
                <PrivateRoute requireSuperAdmin>
                  <UapiPayloadRedactionsPage />
                </PrivateRoute>
              }
            />



            {/* Admin - Currencies. Any signed-in user may view the list; add/edit are superadmin only
                (offered on screen to superadmins, enforced by the admin API). */}
            <Route
              path="/admin/currencies"
              element={
                <PrivateRoute>
                  <Currencies />
                </PrivateRoute>
              }
            />

            {/* Admin › Platform › MCP as a Service — hconnect's default agreement and hotels' proposals (superadmin). */}
            <Route
              path="/admin/mcp-service"
              element={
                <PrivateRoute requireSuperAdmin>
                  <McpAdminPage />
                </PrivateRoute>
              }
            />
            {/* …› a supported source system — the pipeline templates built for it (V644). */}
            <Route
              path="/admin/mcp-service/sources/:sourceId"
              element={
                <PrivateRoute requireSuperAdmin>
                  <McpSourcePage />
                </PrivateRoute>
              }
            />

            {/* Admin - Marketplace Action Types (managed lookup) */}
            <Route
              path="/admin/marketplace-action-types"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceActionTypes />
                </PrivateRoute>
              }
            />

            {/* Admin - Marketplace Setting Create Route */}
            <Route
              path="/admin/marketplace/:marketplaceid/setting/new"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceSettingForm mode="create" />
                </PrivateRoute>
              }
            />

            {/* Admin - Marketplace Setting View Route */}
            <Route
              path="/admin/marketplace/:marketplaceid/setting/:id"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceSettingForm mode="view" />
                </PrivateRoute>
              }
            />

            {/* Admin - Marketplace Setting Edit Route */}
            <Route
              path="/admin/marketplace/:marketplaceid/setting/:id/edit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <MarketplaceSettingForm mode="edit" />
                </PrivateRoute>
              }
            />

            {/* Settings Route */}
            <Route
              path="/chain-settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />

            <Route
              path="/partner-settings"
              element={
                <PrivateRoute>
                  <PartnerSettings />
                </PrivateRoute>
              }
            />
            {/* One billing template (V663) — its own page, opened from Partner Settings > Templates > Billing. */}
            <Route
              path="/partner-settings/billing-templates/:templateId"
              element={
                <PrivateRoute>
                  <BillingTemplatePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/partner-hotel-chains"
              element={
                <PrivateRoute>
                  <PartnerHotelChains />
                </PrivateRoute>
              }
            />

            {/* Sync Routes */}
            <Route
              path="/sync"
              element={
                <PrivateRoute>
                  <SyncPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/create"
              element={
                <PrivateRoute requireAdmin>
                  <PartnerStripeGuard>
                    <SyncProjectFormPage mode="create" />
                  </PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/hotel/:hotelid/sync/new"
              element={
                <PrivateRoute requireAdmin>
                  <PartnerStripeGuard>
                    <SyncProjectFormPage mode="create" />
                  </PartnerStripeGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId"
              element={
                <PrivateRoute>
                  <SyncProjectDetailsPage />
                </PrivateRoute>
              }
            />

            {/* Migration Toolkit — part of a sync project whose template uses it (uses_migration_toolkit = Y), working
                through that project's own OPERA Cloud connection. The old stand-alone /toolkit goes to the projects. */}
            <Route path="/toolkit/*" element={<Navigate to="/sync" replace />} />
            <Route
              path="/sync/:syncId/toolkit"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/blocks"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitBlocksPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/ar-accounts"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitArAccountsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/ar-ledger"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitArLedgerPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/guest-ledger"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitGuestLedgerPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/deposit-ledger"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitDepositLedgerPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/deposit-folio"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitDepositFolioPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/check-in"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitCheckInPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/ooo"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitOooPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/hk-details"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitHkDetailsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/refresh-rates"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitRefreshRatesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/same-day-departures"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitSameDayDeparturesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/market-codes"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitMarketCodesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/create-rooms"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitCreateRoomsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/balance-reservations"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitBalanceReservationsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/cancellation-check"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitCancellationCheckPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/history-forecast"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitHistoryForecastPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/ar-ledger-comparison"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitArLedgerComparisonPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/block-grid"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitBlockGridPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/event-management"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitEventManagementPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/pep-guest-balancing"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitPepGuestBalancingPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/pep-group-master"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitPepGroupMasterPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/profile-resync"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitProfileResyncPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/hconnect-oxi"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitHconnectOxiPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/udfs"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitUdfsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/internal-notes"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitInternalNotesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/oxi-mapping"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitOxiMappingPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/config-compare"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitConfigComparePage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/onprem/:key"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitOnPremPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/toolkit/ar-account-types"
              element={
                <PrivateRoute requireSuperAdmin>
                  <ToolkitArAccountTypesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/edit"
              element={
                <PrivateRoute>
                  <SyncProjectFormPage mode="edit" />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/copy"
              element={
                <PrivateRoute requireAdmin>
                  <SyncProjectFormPage mode="copy" />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/task/:taskId/configure"
              element={
                <PrivateRoute>
                  <TaskConfigurationPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/vendors/:id"
              element={
                <PrivateRoute>
                  <VendorDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/vendor-settings"
              element={
                <PrivateRoute>
                  <VendorSettings />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/task/:taskId/code/:code"
              element={
                <PrivateRoute>
                  <ConversionCodeEditPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/task/:taskId/reconciliation"
              element={
                <PrivateRoute>
                  <TaskReconciliationPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/task/:taskId/reconciliation/:recordUuid/log"
              element={
                <PrivateRoute>
                  <MessageLogPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/task/:taskId/analysis"
              element={
                <PrivateRoute>
                  <ErrorAnalysisPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/task/:taskId/report"
              element={
                <PrivateRoute>
                  <SyncReportPage />
                </PrivateRoute>
              }
            />

            {/* Project-level on-site instructions — every extraction task with records, in one
                report. Reached from the superadmin Reports button on the project page. */}
            <Route
              path="/sync/:syncId/reports"
              element={
                <PrivateRoute>
                  <ProjectOnsiteReportPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sync/:syncId/task/:taskId/onsite"
              element={
                <PrivateRoute>
                  <OnsiteInstructionsPage />
                </PrivateRoute>
              }
            />

            {/* The same report, errors only. A separate ROUTE rather than a query string so it can be
                bookmarked, emailed and linked to as its own thing — which is how a property will use
                it — and so the Reports picker treats it as a report like any other. */}
            <Route
              path="/sync/:syncId/task/:taskId/onsite-errors"
              element={
                <PrivateRoute>
                  <OnsiteInstructionsPage errorsOnly />
                </PrivateRoute>
              }
            />

            <Route
              path="/marketing-pack"
              element={
                <PrivateRoute>
                  <MarketingPack />
                </PrivateRoute>
              }
            />

            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

// ONE OLD ADDRESS, THE WHOLE PATH CARRIED ACROSS.
//
// A plain <Navigate> can only send everything to one place, which would land a link to
// /integrations/7/edit on the template LIST — technically not a 404, and still the wrong page. This
// swaps the prefix and keeps the rest, including the query string, so an old link opens what it always
// opened.
const LegacyPathRedirect = ({ from, to }) => {
    const location = useLocation();
    const tail = location.pathname.startsWith(from) ? location.pathname.slice(from.length) : '';
    return <Navigate to={`${to}${tail}${location.search || ''}`} replace />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AccountProvider>
          <AppContent />
        </AccountProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
